import { Component, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { merge, of, take } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as docx from 'docx';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import * as showdown from 'showdown';
import { DeepPersonalityService } from '../../iqservices/deep-personality.service';

@Component({
  selector: 'app-personality',
  templateUrl: './personality.component.html',
  styleUrls: ['./personality.component.scss']
})
export class PersonalityComponent {
  @ViewChild('tabGroup') tabGroup: any;
  private readonly minPromptWords = 100;

  inputText = '';
  prompt = '';
  isLoading = false;
  error: string | null = null;
  messageHistory: string[] = [];

  mistral: string | null = null;
  cohereRPlus: string | null = null;
  cohere: string | null = null;
  summary: string | null = null;

  formattedMistral: SafeHtml | string = '';
  formattedCohereRPlus: SafeHtml | string = '';
  formattedCohere: SafeHtml | string = '';
  formattedSummary: SafeHtml | string = '';

  toolsMenuOpen = false;
  selectedTabIndex = 0;
  selectedModels: Array<'mistral' | 'cohere'> = ['mistral', 'cohere'];
  rawOutputs: Array<string | null> = [null, null];
  formattedOutputs: Array<SafeHtml | string> = ['', ''];
  tabStatuses: Array<'idle' | 'success' | 'error'> = ['idle', 'idle'];
  summarySucceeded = false;
  summaryError: string | null = null;
  submittedText: string | null = null;
  formattedSubmittedText: SafeHtml | string = '';

  converter: showdown.Converter;

  constructor(
    private deepPersonality: DeepPersonalityService,
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar
  ) {
    this.converter = new showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true
    });
  }

  onSubmit(): void {
    if (!this.inputText.trim()) {
      this.snackBar.open('Please enter a prompt to analyze.', 'Close', {
        duration: 2500
      });
      return;
    }

    const wordCount = this.inputText.trim().split(/\s+/).length;
    if (wordCount < this.minPromptWords) {
      this.snackBar.open(`Prompt must be at least ${this.minPromptWords} words.`, 'Close', {
        duration: 3000
      });
      return;
    }

    if (this.isLoading) {
      return;
    }

    this.prompt = this.inputText.trim();
    this.submittedText = this.prompt;
    this.formattedSubmittedText = this.toHtml(this.submittedText);
    this.messageHistory = [this.prompt, ...this.messageHistory].slice(0, 10);
    this.selectedTabIndex = 0;
    this.inputText = '';
    this.runPersonalityModels();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSubmit();
    }
  }

  onTabIndexChange(index: number): void {
    this.selectedTabIndex = index;
  }

  private runPersonalityModels(): void {
    this.mistral = null;
    this.cohereRPlus = null;
    this.summary = null;
    this.rawOutputs = [null, null];
    this.formattedOutputs = ['', ''];
    this.tabStatuses = ['idle', 'idle'];
    this.summarySucceeded = false;
    this.summaryError = null;
    this.error = null;
    this.isLoading = true;

    const requests = [
      this.deepPersonality.mistralMonsterPersonAssessment(this.prompt).pipe(
        take(1),
        map((r) => ({ idx: 0 as const, text: this.parseMistralResponse(r) })),
        catchError((err) => of({ idx: 0 as const, text: `(Mistral) ${this.errString(err)}` }))
      ),
      this.deepPersonality.cohereMonsterPersonAssessment(this.prompt).pipe(
        take(1),
        map((r) => ({ idx: 1 as const, text: this.parseCohereResponse(r) })),
        catchError((err) => of({ idx: 1 as const, text: `(Cohere R+) ${this.errString(err)}` }))
      )
    ];

    let pending = requests.length;
    let firstSuccessfulTabShown = false;

    merge(...requests).subscribe({
      next: ({ idx, text }) => {
        this.rawOutputs[idx] = text;
        this.formattedOutputs[idx] = this.toHtml(text);

        const isError = idx === 0 ? text.startsWith('(Mistral)') : text.startsWith('(Cohere R+)');
        this.tabStatuses[idx] = isError ? 'error' : 'success';

        if (!firstSuccessfulTabShown && !isError) {
          firstSuccessfulTabShown = true;
          const tabIndex = idx + 1;
          this.selectedTabIndex = tabIndex;
          if (this.tabGroup) this.tabGroup.selectedIndex = tabIndex;
        }

        this.mistral = this.rawOutputs[0];
        this.cohereRPlus = this.rawOutputs[1];
        this.formattedMistral = this.formattedOutputs[0];
        this.formattedCohereRPlus = this.formattedOutputs[1];

        pending -= 1;
        if (pending > 0) {
          return;
        }

        this.getCombinedSummary({
          mistral: this.rawOutputs[0] || '',
          cohereRPlus: this.rawOutputs[1] || ''
        });
      },
      error: (error) => {
        this.error = `Error: ${this.errString(error)}`;
        this.isLoading = false;
      }
    });
  }

  private getCombinedSummary(data: {
    mistral: string;
    cohereRPlus: string;
  }): void {
    const prompt = `Provide a concise personality summary of the following data without repeating content:
      Cohere R+: ${data.cohereRPlus}
      Mistral: ${data.mistral}`;

    this.deepPersonality.callCombinedSummary(prompt, data).subscribe({
      next: (res) => {
        const summary = res && typeof res.summary === 'string' ? res.summary.trim() : '';
        this.summarySucceeded = !!summary;
        if (this.summarySucceeded) {
          this.summary = summary;
          this.formattedSummary = this.toHtml(summary);
          this.summaryError = null;
          this.selectedTabIndex = 0;
          if (this.tabGroup) this.tabGroup.selectedIndex = 0;
        } else {
          this.summary = null;
          this.formattedSummary = '';
          this.summaryError = 'Summary returned no content.';
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.summary = null;
        this.formattedSummary = '';
        this.summarySucceeded = false;
        this.summaryError = `(Summary error) ${this.errString(error)}`;
        this.isLoading = false;
      }
    });
  }

  private errString(err: any): string {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    return err?.message || err?.error?.message || JSON.stringify(err);
  }

  private parseMistralResponse(response: any): string {
    try {
      const data = typeof response === 'string' ? JSON.parse(response) : response;
      return data?.model_output ?? 'No Data';
    } catch (error) {
      console.error('Error parsing Mistral response:', error);
      return 'Invalid response from Mistral';
    }
  }

  private parseCohereResponse(response: any): string {
    try {
      const data = typeof response === 'string' ? JSON.parse(response) : response;
      return data?.model_output ?? data?.summary ?? 'No Data';
    } catch (error) {
      console.error('Error parsing Cohere response:', error);
      return 'Invalid response from Cohere R+';
    }
  }

  private toHtml(text: string | null): SafeHtml {
    try {
      const html = this.converter.makeHtml(text || '');
      return this.sanitizer.bypassSecurityTrustHtml(html);
    } catch (error) {
      console.error('Error converting Markdown to HTML:', error);
      return this.sanitizer.bypassSecurityTrustHtml(text || '');
    }
  }

  tabLabel(modelId: 'mistral' | 'cohere'): string {
    return modelId === 'mistral' ? 'Writer X5' : 'Claude Sonnet';
  }

  getVendorLogo(modelId: 'mistral' | 'cohere'): string {
    return modelId === 'mistral' ? 'assets/logos/x5-baby.png' : 'assets/logos/anthropic.svg';
  }

  placeholderHint(modelId: 'mistral' | 'cohere'): string {
    return modelId === 'mistral'
      ? 'Awaiting Writer X5 personality traits...'
      : 'Awaiting Claude Sonnet personality traits...';
  }

  exportToPDF(): void {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    doc.setFont('Helvetica');
    doc.setFontSize(14);
    doc.setTextColor(40);

    const margin = 40;
    const pageHeight = doc.internal.pageSize.height;

    const addSection = (title: string, content: string, yOffset: number): number => {
      doc.setFont(undefined, 'bold');
      doc.text(title, margin, yOffset);
      doc.setFont(undefined, 'normal');

      const lines = doc.splitTextToSize(this.formatParagraphs(content || ''), 500);

      lines.forEach((line) => {
        const lineHeight = 18;
        if (yOffset + lineHeight > pageHeight - margin) {
          doc.addPage();
          yOffset = margin;
        }
        doc.text(line, margin, yOffset);
        yOffset += lineHeight;
      });

      return yOffset + 20;
    };

    let y = margin;
    y = addSection('Personality Summary', this.summary || '', y);
    y = addSection('Cohere R+', this.cohereRPlus || '', y);
    y = addSection('Mistral', this.mistral || '', y);

    doc.save('personality-summary.pdf');
  }

  exportToDOCX(): void {
    const createParagraphs = (title: string, content: string): docx.Paragraph[] => {
      const paragraphs: docx.Paragraph[] = [];
      paragraphs.push(
        new docx.Paragraph({
          text: title,
          heading: docx.HeadingLevel.HEADING_2,
          spacing: { after: 300 }
        })
      );

      const splitSentences = this.formatParagraphs(content || '').split('\n');
      splitSentences.forEach((sentence) => {
        paragraphs.push(
          new docx.Paragraph({
            text: sentence.trim(),
            spacing: { after: 200 },
            style: 'Normal',
            indent: { left: 720 }
          })
        );
      });

      return paragraphs;
    };

    const document = new docx.Document({
      sections: [
        {
          properties: {},
          children: [
            ...createParagraphs('Personality Summary', this.summary || ''),
            ...createParagraphs('Cohere R+', this.cohereRPlus || ''),
            ...createParagraphs('Mistral', this.mistral || '')
          ]
        }
      ]
    });

    docx.Packer.toBlob(document).then((blob) => {
      saveAs(blob, 'personality-summary.docx');
    });
  }

  private formatParagraphs(text: string): string {
    const sentences = text.split(/(?<=[.!?])\s+/);
    const grouped = sentences.reduce((acc, sentence, index) => {
      const line = sentence.trim();
      if (!line) return acc;
      if (index % 6 === 0 && acc.length) acc.push('\n');
      acc[acc.length - 1] = (acc[acc.length - 1] || '') + ' ' + line;
      return acc;
    }, ['']);
    return grouped.join('\n');
  }
}
